import api from "./axios";

const BASE = "/accounts";
const BLOB = { responseType: "blob" };
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

/** Multipart body for endpoints that accept an attachment. */
function toFormData(payload = {}, fileField = "attachment") {
  const form = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (key === fileField && value instanceof File) {
      form.append(fileField, value);
    } else if (typeof value === "object" && !(value instanceof File)) {
      // Nested objects (the invoice schedule) go over as schedule[field].
      Object.entries(value).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") form.append(`${key}[${k}]`, v);
      });
    } else {
      form.append(key, value);
    }
  });
  return form;
}

/* --- Company Expenses --- */
export const listCompanyExpenses = (params = {}) =>
  api.get(`${BASE}/company-expenses`, { params });
export const getCompanyExpenseKpis = (params = {}) =>
  api.get(`${BASE}/company-expenses/kpis`, { params });
export const createCompanyExpense = (payload) =>
  api.post(`${BASE}/company-expenses`, toFormData(payload), MULTIPART);
export const updateCompanyExpense = (id, payload) =>
  api.put(`${BASE}/company-expenses/${id}`, toFormData(payload), MULTIPART);
export const deleteCompanyExpense = (id) => api.delete(`${BASE}/company-expenses/${id}`);
export const exportCompanyExpenses = (params = {}) =>
  api.get(`${BASE}/company-expenses/export`, { params, ...BLOB });

/* --- Recurring Expenses --- */
export const listRecurringExpenses = (params = {}) =>
  api.get(`${BASE}/recurring-expenses`, { params });
export const createRecurringExpense = (payload) =>
  api.post(`${BASE}/recurring-expenses`, payload);
export const updateRecurringExpense = (id, payload) =>
  api.put(`${BASE}/recurring-expenses/${id}`, payload);
export const pauseRecurringExpense = (id) =>
  api.put(`${BASE}/recurring-expenses/${id}/pause`);
export const resumeRecurringExpense = (id) =>
  api.put(`${BASE}/recurring-expenses/${id}/resume`);
export const deleteRecurringExpense = (id) =>
  api.delete(`${BASE}/recurring-expenses/${id}`);
export const exportRecurringExpenses = (params = {}) =>
  api.get(`${BASE}/recurring-expenses/export`, { params, ...BLOB });

/* --- COD Repay --- */
export const listCodRepayments = (params = {}) => api.get(`${BASE}/cod-repay`, { params });
export const getCodRepaymentKpis = (params = {}) =>
  api.get(`${BASE}/cod-repay/kpis`, { params });
export const createCodRepayment = (payload) =>
  api.post(`${BASE}/cod-repay`, toFormData(payload), MULTIPART);
export const updateCodRepayment = (id, payload) =>
  api.put(`${BASE}/cod-repay/${id}`, toFormData(payload), MULTIPART);
export const deleteCodRepayment = (id) => api.delete(`${BASE}/cod-repay/${id}`);
export const exportCodRepayments = (params = {}) =>
  api.get(`${BASE}/cod-repay/export`, { params, ...BLOB });

/* --- Invoices --- */
export const listInvoices = (params = {}) => api.get(`${BASE}/invoices`, { params });
export const getInvoiceKpis = (params = {}) => api.get(`${BASE}/invoices/kpis`, { params });
export const createInvoice = (payload) => api.post(`${BASE}/invoices`, payload);
export const updateInvoice = (id, payload) => api.put(`${BASE}/invoices/${id}`, payload);
export const deleteInvoice = (id) => api.delete(`${BASE}/invoices/${id}`);
export const downloadInvoicePdf = (id) => api.get(`${BASE}/invoices/${id}/pdf`, BLOB);
export const regenerateInvoicePdf = (id) => api.post(`${BASE}/invoices/${id}/generate-pdf`);
export const exportInvoices = (params = {}) =>
  api.get(`${BASE}/invoices/export`, { params, ...BLOB });

/* --- Ledger --- */
export const listLedger = (params = {}) => api.get(`${BASE}/ledger`, { params });
export const getLedgerKpis = (params = {}) => api.get(`${BASE}/ledger/kpis`, { params });
export const createLedgerEntry = (payload) =>
  api.post(`${BASE}/ledger`, toFormData(payload), MULTIPART);
export const updateLedgerEntry = (id, payload) => api.put(`${BASE}/ledger/${id}`, payload);
export const deleteLedgerEntry = (id) => api.delete(`${BASE}/ledger/${id}`);
export const exportLedger = (params = {}) =>
  api.get(`${BASE}/ledger/export`, { params, ...BLOB });

/* --- Incentives --- */
export const listIncentives = (params = {}) => api.get(`${BASE}/incentives`, { params });
export const getIncentiveKpis = (params = {}) =>
  api.get(`${BASE}/incentives/kpis`, { params });
export const createIncentive = (payload) => api.post(`${BASE}/incentives`, payload);
export const updateIncentive = (id, payload) => api.put(`${BASE}/incentives/${id}`, payload);
export const deleteIncentive = (id) => api.delete(`${BASE}/incentives/${id}`);
export const downloadIncentiveTemplate = () => api.get(`${BASE}/incentives/template`, BLOB);
export const uploadIncentives = (file, { month, year }) => {
  const form = new FormData();
  form.append("file", file);
  form.append("month", month);
  form.append("year", year);
  return api.post(`${BASE}/incentives/upload`, form, MULTIPART);
};
export const exportIncentives = (params = {}) =>
  api.get(`${BASE}/incentives/export`, { params, ...BLOB });

/* --- Employee Salary --- */
export const listEmployeeSalaries = (params = {}) =>
  api.get(`${BASE}/employee-salary`, { params });
export const getEmployeeSalaryKpis = (params = {}) =>
  api.get(`${BASE}/employee-salary/kpis`, { params });
export const createEmployeeSalary = (payload) =>
  api.post(`${BASE}/employee-salary`, payload);
export const updateEmployeeSalary = (id, payload) =>
  api.put(`${BASE}/employee-salary/${id}`, payload);
export const deleteEmployeeSalary = (id) => api.delete(`${BASE}/employee-salary/${id}`);
export const exportEmployeeSalaries = (params = {}) =>
  api.get(`${BASE}/employee-salary/export`, { params, ...BLOB });
export const downloadSalaryTemplate = () =>
  api.get(`${BASE}/employee-salary/template`, BLOB);
export const uploadEmployeeSalaries = (file, { month, year }) => {
  const form = new FormData();
  form.append("file", file);
  form.append("month", month);
  form.append("year", year);
  return api.post(`${BASE}/employee-salary/upload`, form, MULTIPART);
};
